import FileUploadIcon from "@mui/icons-material/FileUpload";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useFileUpload from "../../../../hooks/useFileUpload";
import { useSelectedOrganization } from "../../../../hooks/useSelectedOrganization";
import {
  AssociateValidationErrors,
  useImportAssociatesMutation,
} from "../../../../redux/api/AssociatesApi";
import { useGetAssociateGroupsQuery } from "../../../../redux/api/AssociateGroupsApi";
import ImportErrorDialog from "../../../modal/ImportErrorDialog";
import UploadTooltip from "../../UploadTooltip";

interface FormValues {
  groupId: string;
  file: File;
}

const UploadButton = () => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { enqueueSnackbar } = useSnackbar();

  const [importAssociates, { isLoading }] = useImportAssociatesMutation();
  const organization = useSelectedOrganization();
  const { data } = useGetAssociateGroupsQuery({
    organizationId: organization.publicId,
    sortOrder: "asc",
    sortField: "name",
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    trigger,
  } = useForm<FormValues>();

  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [selectionModalOpen, setSelectionModalOpen] = useState(false);

  const [validationErrors, setValidationErrors] =
    useState<AssociateValidationErrors>({});

  const onErrorModalClose = () => {
    setErrorModalOpen(false);
  };

  const onSelectionModalClose = () => {
    setSelectionModalOpen(false);
  };

  const onUploadClick = () => {
    setSelectionModalOpen(true);
  };

  const onFileSelected = async (files: File[] | FileList) => {
    setValue("file", files[0]);
    handleSubmit(onSubmit)();
  };

  const onSubmit = async ({ groupId, file }: FormValues) => {
    try {
      const associates = await importAssociates({
        file,
        organizationId: organization.publicId,
        groupId,
      }).unwrap();
      onSelectionModalClose();
      enqueueSnackbar(
        t("upload.successful", {
          count: associates.length,
        }),
        {
          variant: "success",
        }
      );
    } catch (err: any) {
      let message = "upload.failed";
      if (err.data?.message) {
        if (err.data?.validationErrors) {
          setValidationErrors(err.data?.validationErrors);
          setErrorModalOpen(true);
        }
        message = err.data?.message;
      }
      enqueueSnackbar(t(message), {
        variant: "error",
        key: err.status,
        preventDuplicate: true,
      });
    }
  };

  const { openDialog } = useFileUpload({
    inputRef: fileInputRef,
    onFileSelected,
  });

  const onFileUpload = async () => {
    const valid = await trigger();
    if (valid) {
      openDialog();
    }
  };

  const groups = useMemo(() => data?.content || [], [data]);

  return (
    <div id="upload-groups-button">
      <UploadTooltip>
        <Button
          startIcon={<FileUploadIcon />}
          onClick={onUploadClick}
          variant="outlined"
          size="medium"
          color="secondary"
        >
          {t("upload.tooltip")}
        </Button>
      </UploadTooltip>
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,.ldif"
        multiple={false}
        hidden
      />
      <ImportErrorDialog
        onClose={onErrorModalClose}
        open={errorModalOpen}
        entries={validationErrors}
      />
      <Dialog
        open={selectionModalOpen}
        onClose={onSelectionModalClose}
        aria-labelledby="upload-dialog-title"
      >
        <DialogTitle id="upload-dialog-title">
          {t("associates.groups.upload.title")}
        </DialogTitle>
        <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
          <DialogContent>
            <DialogContentText>
              {t("associates.groups.upload.description")}
            </DialogContentText>
            <Controller
              name="groupId"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t("validation.group.blank"),
                },
              }}
              render={({ field }) => (
                <FormControl
                  error={!!errors.groupId}
                  fullWidth
                  margin="normal"
                  required
                >
                  <InputLabel id="group-label">
                    {t("associate.group")}
                  </InputLabel>
                  <Select
                    id="group-select"
                    labelId="group-label"
                    label={t("associate.group")}
                    required
                    {...field}
                  >
                    {groups.map(({ publicId, name }) => (
                      <MenuItem key={publicId} value={publicId}>
                        {name}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{errors.groupId?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={onSelectionModalClose}
              variant="outlined"
              size="medium"
              color="secondary"
            >
              {t("cancel")}
            </Button>
            <LoadingButton
              variant="contained"
              size="medium"
              onClick={onFileUpload}
              loading={isLoading}
              color="primary"
            >
              {t("upload.tooltip")}
            </LoadingButton>
          </DialogActions>
        </Box>
      </Dialog>
    </div>
  );
};

export default UploadButton;
